import * as React from 'react';
import BaseInput from '../../../../components/BaseInput';
import Grid from '@mui/material/Grid2';
import BaseButton from '../../../../components/BaseButton';
import BaseRadioButtons from '../../../../components/BaseRadioButtons';
import BaseSelect from '../../../../components/BaseSelect';
import {
  GTAC,
  LAUNCH_BUTTON,
  PREVIEW_BUTTON,
  TCGA,
  TCGA_GTAC,
} from '../../../../constants/base';
import DataStore from '../../../../store';
import { observer } from 'mobx-react';
import BaseCheckbox from '../../../../components/BaseCheckbox';
import { Box, FormLabel, Typography } from '@mui/material';
import { validateForm } from '../../../../helpers/formValidator';
import { notificationStore } from '../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../helpers/constants';
import socketStore from '../../../../store/Socket';
import ProgressBar from '../../../../components/ProgressBar';
import { SOCKET_USER_UPDATE_EVENT } from '../../../../constants/base';
import { validationRules } from '../../../../helpers/validationRules';
import { confirmDialogStore } from '../../../../store/ConfirmDialog';
import {
  getDefaultConfigValue,
  getPageConfiguration,
  isElementHidden,
} from '../../../../helpers/groups';
import BaseTextField from '../../../../components/BaseTextField';
import { useSocketListener } from '../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../types/global';
const DISABLED_ELEMENTS: string[] = [
  'referenceDatabase',
  'threshold',
  TCGA,
  GTAC,
  TCGA_GTAC,
  'fecal',
  'ecoli',
  'mock',
  'sfRange',
  'sfOptimizer',
  'annotatedMosaics',
  'mergeWells',
  'deliveryReport',
  'labelling',
  'sampleType',
  'classificationMode',
  'significant',
  'unique',
];

interface DataState {
  inputPath: string;
  labelling: string;
  sampleType: string;
  classificationMode: string;
  slideNumber: string;
  wells: string;
  threshold: string;
  outputPrefix: string;
  pipeline: string;
  referenceDatabase: string;
  sfRange: boolean;
  sfOptimizer: boolean;
  annotatedMosaics: boolean;
  mergeWells: boolean;
  deliveryReport: boolean;
  sfRangeMin: string;
  sfRangeMax: string;
  sampleId: string;
  configPath: string;
  multipleInput: boolean;
  multipleLines?: number;
}

interface PreLoadDataState {
  inputPath?: string;
  labelling?: string;
  sampleType?: string;
  classificationMode?: string;
  slideNumber?: string;
  wells?: string;
  threshold?: string;
  outputPrefix?: string;
  pipeline?: string;
  referenceDatabase?: string;
  sfRange?: boolean;
  sfOptimizer?: boolean;
  annotatedMosaics?: boolean;
  mergeWells?: boolean;
  deliveryReport?: boolean;
  sfRangeMin?: string;
  sfRangeMax?: string;
  sampleId?: string;
  configPath?: string;
  multipleInput?: boolean;
  multipleLines?: number;
}
const Pipelines = observer(
  ({
    disableSocket = false,
    preLoadData,
    customPreview,
    customLaunch,
    customProgressBar,
  }: {
    disableSocket?: boolean;
    preLoadData?: PreLoadDataState;
    customPreview?: any;
    customLaunch?: any;
    customProgressBar?: any;
  }) => {
    const [progress, setProgress] = React.useState(0);
    const [progressMax, setProgressMax] = React.useState(0);
    const [progressMessage, setProgressMessage] = React.useState('');
    const sfRangeInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
      min: 1,
      max: 2,
      step: 0.1,
      inputMode: 'decimal', // now correctly typed
      pattern: '[0-9]*[.]?[0-9]*',
    };
    const resetProgressBar = () => {
      setProgress(0);
      setProgressMax(0);
      setProgressMessage('');
    };

    React.useEffect(() => {
      if (preLoadData) {
        setPreloadData(preLoadData);
      }
    }, [preLoadData]);

    React.useEffect(() => {
      if (!disableSocket) {
        showOutput();
      }
    }, [disableSocket]);
    const socket = socketStore.socket;
    const channel = React.useMemo(() => SOCKET_USER_UPDATE_EVENT(), []);
    const onProgress = React.useCallback((data: ProgressPayload): void => {
      setProgress(data.progress ?? 0);
      setProgressMax(data.total ?? 0);
      setProgressMessage(data.message ?? '');
    }, []);
    useSocketListener<ProgressPayload>(
      socket,
      channel,
      onProgress,
      !disableSocket
    );

    const fieldLabels: { [key: string]: string } = {
      inputPath: 'Input Path',
      slideNumber: 'Slide Number',
      wells: 'Wells',
      outputPrefix: 'Output Prefix',
      pipeline: 'Pipeline',
      referenceDatabase: 'Reference Database',
      threshold: 'Threshold',
      labelling: 'Labelling',
      sampleType: 'Sample Type',
      classificationMode: 'Classification Mode',
      sfRangeMin: 'SF Range Min',
      sfRangeMax: 'SF Range Max',
      sampleId: 'Sample ID',
      configPath: 'Config Path',
      sfRange: 'SF Range',
      sfOptimizer: 'SF Optimizer',
      annotatedMosaics: 'Annotated Mosaics',
      mergeWells: 'Merge Wells',
      deliveryReport: 'Delivery Report',
    };

    const validationSchema: any = {
      ...{
        inputPath: validationRules.inputPath,
        slideNumber: validationRules.slideNumber,
        outputPrefix: validationRules.outputPrefix,
        wells: validationRules.wells,
      },
      pipeline: {
        subElements: {
          labelling: { type: 'string' },
          sampleType: { type: 'string' },
          classificationMode: { type: 'string' },
        },
      },
      sfRange: {
        subElements: {
          sfRangeMin: { type: 'number' },
          sfRangeMax: { type: 'number' },
        },
        type: 'boolean',
      },
      deliveryReport: {
        type: 'boolean',
      },
      referenceDatabase: {
        type: 'string',
      },
      threshold: {
        type: 'number',
        min: 0,
        max: 1,
        message: 'Threshold must be a number between 0 and 1.',
        required: false,
      },
    };

    const pageConfiguration: any = getPageConfiguration('pipelines');

    const defaultData: DataState = {
      inputPath: '',
      labelling: pageConfiguration?.labeling?.defaultValue ?? 'GTAC',
      sampleType: 'Fecal',
      classificationMode: 'significant',
      slideNumber: '',
      wells: '',
      threshold: '',
      outputPrefix: '',
      pipeline: 'segment_classify',
      referenceDatabase: 'Human Gut Microbiome Standard v10.1',
      sfRange: false,
      sfOptimizer: getDefaultConfigValue('pipelines', 'sfOptimizer', true),
      annotatedMosaics: false,
      mergeWells: false,
      deliveryReport: getDefaultConfigValue(
        'pipelines',
        'deliveryReport',
        false
      ),
      sfRangeMin: '',
      sfRangeMax: '',
      sampleId: '',
      configPath: '',
      multipleInput: false,
      multipleLines: 1,
    };

    const [disabledElements, setDisabledElements] = React.useState<string[]>([
      'TCGA-GTAC',
    ]);
    const [errors, setErrors] = React.useState({});
    const [data, setData] = React.useState<DataState>(defaultData);

    const setPreloadData = (loadData: any) => {
      if (loadData) {
        setData((prevData: any) => ({
          ...prevData,
          ...loadData,
        }));
      }
    };

    const previewRun = async (e: any) => {
      e.preventDefault();
      setProgress(0);
      setProgressMax(100);
      setProgressMessage('Starting...');

      const validError = validateForm(data, validationSchema, disabledElements);

      if (!validError) {
        setErrors({});
        if (customPreview !== undefined) {
          await customPreview(getFilteredData());
        } else {
          const resData: any =
            await DataStore.pipelinePreviewRequest(getFilteredData());
          if (resData?.messageType === 'error') {
            resetProgressBar();
            showOutput(resData, 'Pipeline preview');
          } else if (resData) {
            showOutput(resData, 'Pipeline preview');
          }
        }
      } else {
        resetProgressBar();

        const isRequiredMissing = (Object.values(validError) as string[]).some(
          (msg) => msg.toLowerCase().includes('is required')
        );

        const humanizedErrors = Object.entries(validError).reduce(
          (acc: any, [key, message]) => {
            const label = fieldLabels[key] || key;
            const safeMessage =
              typeof message === 'string' ? message : String(message);
            acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
            return acc;
          },
          {}
        );

        notificationStore.showMessage(
          isRequiredMissing
            ? MISSING_FIELDS_MESSAGE
            : 'Some fields are invalid.',
          'error',
          humanizedErrors
        );
        setErrors(humanizedErrors);
      }
    };

    const showOutput = (resData: any = {}, title: string = '') => {
      DataStore.showOutput(title, resData, 'html');
    };

    const confirmRequest = async () => {
      const resConfirm = await DataStore.pipelineLaunchRequest({
        confirm: true,
      });
      if (resConfirm.messageType !== 'error') {
        if (resConfirm.response) {
          showOutput(resConfirm, 'Pipeline launch');
        }
      }
    };

    const disableRequest = async () => {
      const resConfirm = await DataStore.pipelineLaunchRequest({
        confirm: false,
      });
      if (resConfirm.messageType !== 'error') {
        if (resConfirm.response) {
          showOutput(resConfirm, 'Pipeline launch');
        }
      }
    };

    const launchRun = async () => {
      setProgress(0);
      setProgressMax(100);
      setProgressMessage('Starting...');
      if (customLaunch !== undefined) {
        await customLaunch(data);
      } else {
        const res = await DataStore.pipelineLaunchRequest();

        if (res.messageType === 'confirm') {
          confirmDialogStore.show(
            'Confirm job launch',
            res.message,
            confirmRequest,
            disableRequest
          );
        } else {
          showOutput(res, 'Pipeline launch');
          setProgressMessage('Finished.');
        }
        if (res) {
          showOutput(res, 'Pipeline launch');
        }
      }
    };

    const resetValueByDisabledElement = (
      pomData: any,
      disabledElements: string[] = []
    ) => {
      disabledElements.forEach((el) => {
        if (el in pomData) {
          const key = el as keyof DataState;
          if (typeof pomData[key] === 'boolean') {
            pomData[key] = false;
          } else if (typeof pomData[key] === 'string') {
            pomData[key] = '';
          } else if (typeof pomData[key] === 'number') {
            pomData[key] = 0;
          }
        }
      });

      return pomData;
    };

    const getFilteredData = (): Partial<DataState> => {
      return Object.keys(data).reduce((obj: any, key: string) => {
        if (!disabledElements.includes(key)) {
          const typedKey = key as keyof DataState;
          if (data[typedKey] !== '') {
            obj[typedKey] = data[typedKey];
          }
        }
        return obj;
      }, {} as Partial<DataState>);
    };

    const onChangeData = (event: any) => {
      const { name, value }: any = event.target;

      if (name.includes('checkbox')) {
      } else {
        setData((prevData: any) => {
          const newDisabledElements: string[] = [];
          const returnData = {
            ...prevData,
            [name]: value,
          };
          setErrors({});

          // Enforce "mock" label if pipeline contains specific values
          if (/d6300|d6322|d6331/.test(returnData.pipeline)) {
            newDisabledElements.push('fecal', 'ecoli');
            returnData.sampleType = 'Mock';
          } else {
            newDisabledElements.push('mock');
            if (returnData.sampleType === 'Mock') {
              returnData.sampleType = '';
            }
          }

          if (!/classify/.test(returnData.pipeline)) {
            newDisabledElements.push(...DISABLED_ELEMENTS);
            returnData.referenceDatabase = '';
            returnData.labelling = '';
          }
          if (/classify/.test(returnData.pipeline)) {
            setDisabledElements([]);

            // Restore defaults when switching from a non-classify pipeline
            if (!/classify/.test(prevData.pipeline)) {
              returnData.classificationMode = defaultData.classificationMode;
              returnData.referenceDatabase = defaultData.referenceDatabase;
              returnData.sfRange = defaultData.sfRange;
              returnData.sfOptimizer = defaultData.sfOptimizer;
              returnData.annotatedMosaics = defaultData.annotatedMosaics;
              returnData.mergeWells = defaultData.mergeWells;
              returnData.deliveryReport = defaultData.deliveryReport;
            }
            if (returnData.sampleType === '') {
              returnData.sampleType = defaultData.sampleType;
            }

            const db = returnData.referenceDatabase;
            const findSelectedDB = getLabelingByReferenceDatabaseValues(db);

            if (
              findSelectedDB &&
              findSelectedDB?.allowed_labelling.length > 0
            ) {
              const forbidden = getLabelingOptions().filter(
                (item: any) => !findSelectedDB?.allowed_labelling.includes(item)
              );
              if (
                findSelectedDB?.allowed_labelling.indexOf(
                  returnData.labelling
                ) === -1
              ) {
                returnData.labelling = findSelectedDB?.allowed_labelling[0];
              } else {
                returnData.labelling = returnData.labelling;
              }

              newDisabledElements.push(...forbidden);
            }
          }

          setDisabledElements(newDisabledElements);
          return resetValueByDisabledElement(returnData, newDisabledElements);
        });
      }
    };

    // Apply the pipeline/database rules to the initial state
    // on first page load
    React.useLayoutEffect(() => {
      onChangeData({ target: { name: 'pipeline', value: data.pipeline } });
    }, []);

    const isDisabled = (name: string) => {
      if (disabledElements.length === 0) {
        return false;
      }
      return disabledElements.some(
        (el) => el.toLowerCase() === name.toLowerCase()
      );
    };

    const getPipelineValues = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');

      return pageConfiguration?.pipeline || [];
    };

    const getLabelingByReferenceDatabaseValues = (name: string) => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return (
        pageConfiguration?.referenceDatabase.find(
          (item: any) => item.label === name
        ) || null
      );
    };

    const getReferenceDatabaseValues = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return (
        pageConfiguration?.referenceDatabase.map((item: any) => item.label) ||
        []
      );
    };

    const showLabeling = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return pageConfiguration?.labelling || false;
    };

    const getLabelingOptions = () => {
      return showLabeling() || [];
    };

    const showSampleTypes = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return pageConfiguration?.sampleType || false;
    };

    const getSampleTypeOptions = (): string[] => {
      const options: string[] = showSampleTypes() || [];
      return options.map(
        (opt: string) => opt.charAt(0).toUpperCase() + opt.slice(1)
      );
    };

    const showClassificationModes = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return pageConfiguration?.classificationMode || false;
    };

    const getClassificationModes = () => {
      const options: string[] = showClassificationModes() || [];
      return options.map((opt: string) => ({
        label: opt.charAt(0).toUpperCase() + opt.slice(1),
        value: opt,
      }));
    };

    const showOtherOptions = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return pageConfiguration?.otherOptions.length > 0;
    };

    const showOtherOptionsField = (field: string) => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return pageConfiguration?.otherOptions.indexOf(field) !== -1;
    };

    const showTreshold = () => {
      const pageConfiguration: any = getPageConfiguration('pipelines');
      return pageConfiguration?.threshold || false;
    };

    return (
      <>
        <Grid container spacing={1.5}>
          <Grid size={12}>
            {preLoadData ? (
              <BaseTextField
                feature={'pipelines'}
                label='S3 input paths'
                name={'inputPath'}
                value={data.inputPath}
                readOnly={true}
                multipleLines={data.multipleLines ?? 1}
              />
            ) : (
              <BaseInput
                feature={'pipelines'}
                label='S3 input path'
                value={data.inputPath}
                name={'inputPath'}
                onChange={onChangeData}
                errors={errors}
                required={!!validationSchema?.inputPath}
              />
            )}
          </Grid>
          {preLoadData ? null : (
            <>
              <Grid size={{ xs: 6, md: 3 }}>
                <BaseInput
                  feature={'pipelines'}
                  label={fieldLabels.slideNumber}
                  value={data.slideNumber}
                  name={'slideNumber'}
                  onChange={onChangeData}
                  errors={errors}
                  type='text'
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <BaseInput
                  feature={'pipelines'}
                  label={fieldLabels.wells}
                  value={data.wells}
                  name={'wells'}
                  onChange={onChangeData}
                  errors={errors}
                  type='text'
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <BaseInput
              feature={'pipelines'}
              label={fieldLabels.outputPrefix}
              value={data.outputPrefix}
              name={'outputPrefix'}
              onChange={onChangeData}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12 }}>
            <BaseSelect
              feature={'pipelines'}
              name='pipeline'
              label={fieldLabels.pipeline}
              values={getPipelineValues()}
              value={data.pipeline}
              onChange={onChangeData}
              errors={errors}
              required={!!validationSchema?.pipeline}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <BaseSelect
              feature={'pipelines'}
              name={'referenceDatabase'}
              label={fieldLabels.referenceDatabase}
              values={getReferenceDatabaseValues()}
              value={data.referenceDatabase}
              onChange={onChangeData}
              errors={errors}
              required={
                !!validationSchema?.referenceDatabase &&
                disabledElements.indexOf('referenceDatabase') === -1
              }
              isDisabled={isDisabled('referenceDatabase')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {showTreshold() ? (
              <BaseInput
                feature={'pipelines'}
                label={fieldLabels.threshold}
                value={data.threshold}
                name='threshold'
                onChange={onChangeData}
                errors={errors}
                type='number'
                inputProps={{
                  step: 0.1,
                  min: 0,
                  max: 1,
                }}
                isDisabled={isDisabled('threshold')}
              />
            ) : null}
          </Grid>

          <Grid container spacing={0}>
            {showLabeling() ? (
              <Grid size={{ xs: 12, md: 12 }}>
                <BaseRadioButtons
                  items={getLabelingOptions()}
                  label={fieldLabels.labelling}
                  value={data.labelling}
                  name={'labelling'}
                  onChange={onChangeData}
                  isDisabled={isDisabled}
                  required={
                    !!validationSchema?.pipeline?.subElements?.labelling &&
                    !isDisabled('labelling')
                  }
                  errors={errors}
                />
              </Grid>
            ) : null}
            {showSampleTypes() ? (
              <Grid size={{ xs: 12, md: 12 }}>
                <BaseRadioButtons
                  items={getSampleTypeOptions()}
                  label={fieldLabels.sampleType}
                  value={data.sampleType}
                  name={'sampleType'}
                  onChange={onChangeData}
                  isDisabled={isDisabled}
                  required={
                    !!validationSchema?.pipeline?.subElements?.sampleType &&
                    !isDisabled('sampleType')
                  }
                  errors={errors}
                />
              </Grid>
            ) : null}
            {showClassificationModes() ? (
              <Grid size={{ xs: 12, md: 12 }}>
                <BaseRadioButtons
                  items={getClassificationModes()}
                  label={fieldLabels.classificationMode}
                  value={data.classificationMode}
                  name={'classificationMode'}
                  onChange={onChangeData}
                  isDisabled={isDisabled}
                  required={
                    !!validationSchema?.pipeline?.subElements
                      ?.classificationMode && !isDisabled('classificationMode')
                  }
                  errors={errors}
                />
              </Grid>
            ) : null}
            {showOtherOptions() ? (
              <Grid size={{ xs: 12, md: 12 }}>
                <FormLabel component='legend'>{'Other options'}</FormLabel>
                {showOtherOptionsField('sfRange') ? (
                  <>
                    <BaseCheckbox
                      name='sfRange'
                      label={fieldLabels.sfRange}
                      onChange={onChangeData}
                      value={data.sfRange}
                      isDisabled={isDisabled('sfRange')}
                    />
                    {data.sfRange ? (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6, md: 6 }}>
                          <BaseInput
                            label='min'
                            type='number'
                            value={data.sfRangeMin}
                            name={'sfRangeMin'}
                            onChange={onChangeData}
                            errors={errors}
                            required={true}
                            inputProps={sfRangeInputProps}
                          />
                        </Grid>

                        <Grid size={{ xs: 6, md: 6 }}>
                          <BaseInput
                            label='max'
                            type='number'
                            value={data.sfRangeMax}
                            name={'sfRangeMax'}
                            onChange={onChangeData}
                            errors={errors}
                            required={true}
                            inputProps={sfRangeInputProps}
                          />
                        </Grid>
                      </Grid>
                    ) : null}
                  </>
                ) : null}

                {showOtherOptionsField('sfOptimizer') &&
                !isElementHidden('pipelines', 'sfOptimizer') ? (
                  <BaseCheckbox
                    name='sfOptimizer'
                    label={fieldLabels.sfOptimizer}
                    onChange={onChangeData}
                    value={data.sfOptimizer}
                    isDisabled={isDisabled('sfOptimizer')}
                  />
                ) : null}
                {showOtherOptionsField('annotatedMosaics') ? (
                  <BaseCheckbox
                    name='annotatedMosaics'
                    label={fieldLabels.annotatedMosaics}
                    onChange={onChangeData}
                    value={data.annotatedMosaics}
                    isDisabled={isDisabled('annotatedMosaics')}
                  />
                ) : null}
                {showOtherOptionsField('mergeWells') ? (
                  <BaseCheckbox
                    name='mergeWells'
                    label={fieldLabels.mergeWells}
                    onChange={onChangeData}
                    value={data.mergeWells}
                    isDisabled={isDisabled('mergeWells')}
                  />
                ) : null}
                {showOtherOptionsField('deliveryReport') ? (
                  <BaseCheckbox
                    name='deliveryReport'
                    label={fieldLabels.deliveryReport}
                    onChange={onChangeData}
                    value={data.deliveryReport}
                    isDisabled={isDisabled('deliveryReport')}
                  />
                ) : null}
              </Grid>
            ) : null}
          </Grid>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box display='flex' alignItems='center' gap={1}>
              <BaseButton
                text={PREVIEW_BUTTON}
                variant='outlined'
                color='primary'
                onClick={previewRun}
                isDisabled={DataStore.isProcessing}
                spinner={DataStore.isProcessing}
              />
              <BaseButton
                text={LAUNCH_BUTTON}
                variant='contained'
                color='primary'
                isDisabled={
                  DataStore.isProcessing || !DataStore.isPipelinePreview
                }
                spinner={DataStore.isProcessing}
                onClick={launchRun}
              />
              {DataStore.pipelineCredits ? (
                <Typography style={{ color: 'red' }}>
                  Credits cost: {DataStore.pipelineCredits}
                </Typography>
              ) : null}
            </Box>
          </Grid>
        </Grid>
        {customProgressBar ? (
          customProgressBar()
        ) : (
          <ProgressBar
            actual={progress}
            max={progressMax}
            message={progressMessage}
          />
        )}
      </>
    );
  }
);

export default Pipelines;
